/**
 * batch-70 (FO-530, spec 230) — pure regels rond de sollicitant: één bronlijst
 * (keuzelijst, filter, varianten samenvoegen) en certificaten als vinkmodel
 * (geldigheid markeren, de oude vrije tekst omzetten). Geen I/O; de API en het
 * formulier rekenen met dezelfde functies. Wordt via `recruitment-beoordeling.ts`
 * geëxporteerd (geen eigen regel in de gedeelde index).
 */
import {
  CERTIFICAAT_BIJNA_VERLOPEN_MAANDEN,
  KANDIDAAT_BRON_ANDERS,
  bronUniekSleutel,
  type CertificaatOmzetFragmentDTO,
  type KandidaatBronInGebruik,
} from '@streep/types'
import {
  matchCertificaatType,
  normaliseerCertificaatNaam,
  isVerzamelCertificaatType,
  type CertificaatSynoniemVoorMatch,
  type CertificaatTypeVoorMatch,
} from './certificaat-mailbox'

// ─── Bron van de sollicitant (§A) ───────────────────────────────────────────

export interface BronOptie {
  value: string
  label: string
  /** Een waarde die niet (meer) in de beheerde lijst staat. */
  buitenLijst?: boolean
}

/** Is dit de "Anders"-keuze (vrije tekst)? */
export function isAndersBron(bron: string | null | undefined): boolean {
  return !!bron && bronUniekSleutel(bron) === bronUniekSleutel(KANDIDAAT_BRON_ANDERS)
}

/**
 * Keuzelijst van het nieuw-formulier en de bewerk-sheet: de beheerde lijst (op
 * volgorde, zonder dubbelen), "Anders" altijd als laatste, en een bestaande
 * waarde die niet in de lijst staat bovenaan (zodat opslaan hem niet wist).
 */
export function bronKeuzeOpties(lijst: readonly string[], huidig?: string | null): BronOptie[] {
  const uit: BronOptie[] = []
  const gezien = new Set<string>()
  for (const b of lijst) {
    const naam = b.trim()
    if (!naam || isAndersBron(naam) || gezien.has(bronUniekSleutel(naam))) continue
    gezien.add(bronUniekSleutel(naam))
    uit.push({ value: naam, label: naam })
  }
  uit.push({ value: KANDIDAAT_BRON_ANDERS, label: `${KANDIDAAT_BRON_ANDERS} (zelf invullen)` })
  const h = huidig?.trim()
  if (h && !isAndersBron(h) && !gezien.has(bronUniekSleutel(h))) {
    uit.unshift({ value: h, label: `${h} (niet in de lijst)`, buitenLijst: true })
  }
  return uit
}

/**
 * Begintoestand van de bronkeuze: een waarde uit de lijst (in de spelling van de
 * lijst), of "Anders" met de vrije tekst als hij er niet in staat.
 */
export function bronBeginKeuze(lijst: readonly string[], huidig: string | null | undefined): { keuze: string | null; andersTekst: string } {
  const h = huidig?.trim()
  if (!h) return { keuze: null, andersTekst: '' }
  const inLijst = lijst.find((b) => bronUniekSleutel(b) === bronUniekSleutel(h))
  if (inLijst && !isAndersBron(inLijst)) return { keuze: inLijst.trim(), andersTekst: '' }
  return { keuze: h, andersTekst: '' }
}

/**
 * Van keuze naar op te slaan bron. "Anders" vraagt een eigen tekst (§6.1: anders
 * een inline fout); geen keuze = geen bron.
 */
export function bepaalBronWaarde(keuze: string | null, andersTekst: string): { bron: string | null; fout: string | null } {
  if (!keuze) return { bron: null, fout: null }
  if (isAndersBron(keuze)) {
    const tekst = andersTekst.trim()
    if (!tekst) return { bron: null, fout: 'Beschrijf de bron (bij "Anders" is een eigen tekst verplicht).' }
    if (tekst.length > 100) return { bron: null, fout: 'De bron is maximaal 100 tekens.' }
    return { bron: tekst, fout: null }
  }
  return { bron: keuze.trim(), fout: null }
}

/**
 * Opties van het lijstfilter: de beheerde lijst plus elke waarde die echt op
 * kandidaten voorkomt (ook oude vrije varianten), met het aantal erbij.
 */
export function bronFilterOpties(lijst: readonly string[], inGebruik: readonly KandidaatBronInGebruik[]): BronOptie[] {
  const aantallen = new Map<string, number>()
  for (const g of inGebruik) aantallen.set(bronUniekSleutel(g.bron), (aantallen.get(bronUniekSleutel(g.bron)) ?? 0) + g.aantal)
  const uit: BronOptie[] = []
  const gezien = new Set<string>()
  for (const b of lijst) {
    const naam = b.trim()
    const sleutel = bronUniekSleutel(naam)
    if (!naam || gezien.has(sleutel)) continue
    gezien.add(sleutel)
    uit.push({ value: naam, label: `${naam} (${aantallen.get(sleutel) ?? 0})` })
  }
  const rest = inGebruik
    .filter((g) => !gezien.has(bronUniekSleutel(g.bron)))
    .sort((a, b) => b.aantal - a.aantal || a.bron.localeCompare(b.bron, 'nl'))
  for (const g of rest) {
    if (gezien.has(bronUniekSleutel(g.bron))) continue
    gezien.add(bronUniekSleutel(g.bron))
    uit.push({ value: g.bron, label: `${g.bron} (${aantallen.get(bronUniekSleutel(g.bron)) ?? g.aantal}, niet in de lijst)`, buitenLijst: true })
  }
  return uit
}

/** Sleutel voor varianten: zonder hoofdletters, accenten, spaties en leestekens ("E-mail" = "email"). */
export function bronVarianteSleutel(bron: string): string {
  return bron
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('nl-NL')
    .replace(/[^a-z0-9]+/g, '')
}

export interface BronVariantGroep {
  /** De bron waarin de varianten worden samengevoegd (de spelling uit de lijst, anders de meest gebruikte). */
  naar: string
  /** De afwijkende schrijfwijzen die op kandidaten staan. */
  varianten: KandidaatBronInGebruik[]
  /** Aantal kandidaten met een afwijkende schrijfwijze. */
  aantal: number
}

/**
 * §A1 — varianten die samengevoegd kunnen worden ("linkedin", "LinkedIn ",
 * "Linked-In" -> "LinkedIn"). Alleen groepen met minstens één schrijfwijze die
 * afwijkt van het doel; volgorde: meeste kandidaten eerst.
 */
export function groepeerBronVarianten(inGebruik: readonly KandidaatBronInGebruik[], lijst: readonly string[]): BronVariantGroep[] {
  const groepen = new Map<string, KandidaatBronInGebruik[]>()
  for (const g of inGebruik) {
    const sleutel = bronVarianteSleutel(g.bron)
    if (!sleutel) continue
    groepen.set(sleutel, [...(groepen.get(sleutel) ?? []), g])
  }
  const uit: BronVariantGroep[] = []
  for (const [sleutel, waarden] of groepen) {
    const uitLijst = lijst.find((b) => bronVarianteSleutel(b) === sleutel)?.trim()
    const naar = uitLijst ?? [...waarden].sort((a, b) => b.aantal - a.aantal || a.bron.localeCompare(b.bron, 'nl'))[0]!.bron.trim()
    const varianten = waarden.filter((w) => w.bron !== naar)
    if (varianten.length === 0) continue
    uit.push({ naar, varianten, aantal: varianten.reduce((s, v) => s + v.aantal, 0) })
  }
  return uit.sort((a, b) => b.aantal - a.aantal || a.naar.localeCompare(b.naar, 'nl'))
}

// ─── Certificaten (§C) ──────────────────────────────────────────────────────

export type CertificaatGeldigheid = 'ONBEKEND' | 'GELDIG' | 'VERLOOPT_BINNENKORT' | 'VERLOPEN'

/** 'yyyy-MM-dd' plus n maanden (dag begrensd op de laatste dag van de maand). */
export function ymdPlusMaanden(ymd: string, maanden: number): string {
  const [j, m, d] = ymd.slice(0, 10).split('-').map(Number) as [number, number, number]
  const totaal = j * 12 + (m - 1) + maanden
  const jaar = Math.floor(totaal / 12)
  const maand = (totaal % 12) + 1
  const laatste = new Date(Date.UTC(jaar, maand, 0)).getUTCDate()
  const dag = Math.min(d, laatste)
  return `${jaar}-${String(maand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
}

/**
 * §C5 / §6.2 — markering van een aangevinkt certificaat. Geldig tot en met de
 * datum; verlopen = rood, binnen 3 maanden verlopend = oranje, geen datum =
 * onbekend (grijs). `vandaag` = de NL-kalenderdag ('yyyy-MM-dd').
 */
export function certificaatGeldigheid(geldigTot: string | null | undefined, vandaag: string): CertificaatGeldigheid {
  if (!geldigTot) return 'ONBEKEND'
  const tot = geldigTot.slice(0, 10)
  if (tot < vandaag) return 'VERLOPEN'
  if (tot < ymdPlusMaanden(vandaag, CERTIFICAAT_BIJNA_VERLOPEN_MAANDEN)) return 'VERLOOPT_BINNENKORT'
  return 'GELDIG'
}

/** Een opleidingsrij van de kandidaat, zoals de vinklijst hem leest. */
export interface OpleidingVoorVinkje {
  id: string
  certificateTypeId: string | null
  /** 'yyyy-MM-dd' of volledige ISO; null = onbekend. */
  geldigTot: string | null
  gewenst: boolean
  afgerond: boolean | null
  bevestigd: boolean
}

export interface CertificaatVinkjeStand {
  certificateTypeId: string
  /** De laatste geldig-tot van dit type ('yyyy-MM-dd'), of null. */
  geldigTot: string | null
  /** Alle rijen achter dit vinkje; de eerste is de rij die de vinklijst bijwerkt. */
  opleidingIds: string[]
}

/**
 * §C4 — welke opleidingen een vinkje zijn: met certificaattype, bevestigd, niet
 * "gewenst" (dat is een verwachting van STREEP, geen bezit) en niet als
 * niet-afgerond gemarkeerd. Per type één vinkje; de rij met de laatste
 * geldig-tot voorop (zonder datum achteraan). API (sync, aanname) en formulier
 * gebruiken dezelfde regel.
 */
export function certificaatVinkjes(opleidingen: readonly OpleidingVoorVinkje[]): CertificaatVinkjeStand[] {
  const perType = new Map<string, OpleidingVoorVinkje[]>()
  for (const o of opleidingen) {
    if (!o.certificateTypeId || o.gewenst || o.afgerond === false || !o.bevestigd) continue
    perType.set(o.certificateTypeId, [...(perType.get(o.certificateTypeId) ?? []), o])
  }
  return [...perType.entries()].map(([certificateTypeId, rijen]) => {
    const gesorteerd = [...rijen].sort((a, b) => (b.geldigTot ?? '').slice(0, 10).localeCompare((a.geldigTot ?? '').slice(0, 10)))
    return {
      certificateTypeId,
      geldigTot: gesorteerd[0]!.geldigTot ? gesorteerd[0]!.geldigTot.slice(0, 10) : null,
      opleidingIds: gesorteerd.map((r) => r.id),
    }
  })
}

/**
 * De oude vrije certificatentekst ("VCA, NEN3140 / EHBO en BHV") in losse
 * stukken. Volgorde blijft, dubbelen (hoofdletters tellen niet) vallen weg.
 */
export function splitsCertificaatTekst(tekst: string | null | undefined): string[] {
  const uit: string[] = []
  const gezien = new Set<string>()
  for (const deel of (tekst ?? '').split(/[,;\n\r+&|]+|\s\/\s|\s+en\s+/i)) {
    const fragment = deel.trim().replace(/^[-*•.\s]+|[.\s]+$/g, '')
    if (!fragment) continue
    const sleutel = fragment.toLocaleLowerCase('nl-NL')
    if (gezien.has(sleutel)) continue
    gezien.add(sleutel)
    uit.push(fragment)
  }
  return uit
}

/** Kleine letters, zonder accenten, spaties en leestekens, in de oorspronkelijke volgorde ("NEN 3140 VP" -> "nen3140vp"). */
function compact(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

/**
 * Stel per stuk tekst een certificaattype voor. Eerst de exacte herkenning van
 * de certificaat-mailbox (naam, code, synoniem; `matchCertificaatType`), daarna
 * een ruimere zoekslag op woorden ("VCA" past op VCA Basis en VCA Vol). Meer dan
 * één passend type = MEERDERE: de recruiter kiest, er wordt niets stil ingevuld.
 */
export function stelCertificaatOmzettingVoor(
  tekst: string | null | undefined,
  types: readonly CertificaatTypeVoorMatch[],
  synoniemen: readonly CertificaatSynoniemVoorMatch[],
  aangevinkteTypeIds: Iterable<string>,
): CertificaatOmzetFragmentDTO[] {
  const aangevinkt = new Set(aangevinkteTypeIds)
  const bruikbaar = types.filter((t) => t.isActive && !isVerzamelCertificaatType(t))
  const naamVan = new Map(bruikbaar.map((t) => [t.id, t.name]))
  const sorteer = (ids: readonly string[]) =>
    [...new Set(ids)]
      .filter((i) => naamVan.has(i))
      .sort((a, b) => naamVan.get(a)!.localeCompare(naamVan.get(b)!, 'nl'))
      .map((i) => ({ certificateTypeId: i, naam: naamVan.get(i)! }))
  return splitsCertificaatTekst(tekst).map((fragment): CertificaatOmzetFragmentDTO => {
    const exact = matchCertificaatType(fragment, bruikbaar, synoniemen)
    let ids: string[] = exact.uitkomst === 'ONBEKEND' ? [] : exact.kandidaatIds
    if (ids.length === 0) {
      const woorden = normaliseerCertificaatNaam(fragment).split(' ').filter((w) => w.length > 0)
      const kort = compact(fragment)
      ids = bruikbaar
        .filter((t) => {
          const eigen = new Set(`${normaliseerCertificaatNaam(t.name)} ${normaliseerCertificaatNaam(t.code)}`.split(' ').filter((w) => w.length > 0))
          const opWoorden = woorden.length > 0 && woorden.every((w) => eigen.has(w))
          const opTekst = kort.length >= 3 && (compact(t.name).includes(kort) || compact(t.code).includes(kort))
          return opWoorden || opTekst
        })
        .map((t) => t.id)
    }
    const kandidaten = sorteer(ids)
    const uitkomst = kandidaten.length === 0 ? 'ONBEKEND' : kandidaten.length === 1 ? 'GEVONDEN' : 'MEERDERE'
    const voorstelId = kandidaten[0]?.certificateTypeId ?? null
    return {
      fragment,
      uitkomst,
      kandidaten,
      voorstelId,
      alAangevinkt: kandidaten.some((k) => aangevinkt.has(k.certificateTypeId)) && uitkomst === 'GEVONDEN',
    }
  })
}

/**
 * Wat van de oude tekst overblijft na het omzetten: de stukken die niet zijn
 * omgezet, of null als alles een vinkje werd.
 */
export function restCertificaatTekst(tekst: string | null | undefined, omgezet: Iterable<string>): string | null {
  const weg = new Set([...omgezet].map((f) => f.trim().toLocaleLowerCase('nl-NL')))
  const rest = splitsCertificaatTekst(tekst).filter((f) => !weg.has(f.toLocaleLowerCase('nl-NL')))
  return rest.length > 0 ? rest.join(', ') : null
}

/**
 * Uitgiftedatum voor het medewerker-certificaat bij aanname (het model vraagt er
 * één): het opgegeven jaar, anders terugrekenen vanaf "geldig tot" met de
 * geldigheidsduur van het type, anders vandaag. HR controleert het concept.
 */
export function geschatteUitgifteDatum(input: {
  jaar: number | null
  geldigTot: string | null
  validityMonths: number | null
  vandaag: string
}): string {
  if (input.jaar) return `${input.jaar}-01-01`
  if (input.geldigTot && input.validityMonths) return ymdPlusMaanden(input.geldigTot.slice(0, 10), -input.validityMonths)
  return input.vandaag
}
